import React from 'react';
import './frame-builder.scss';
import {observer} from 'mobx-react';
import {DisplayTypeEnum, Inches, RenderV2Object} from './render-v2-d';
import {Moulding} from './moulding/moulding';
import {InnerContent} from './inner-content/inner-content';
import {CustomItem} from './custom-item/custom-item';
import {FrameOld} from '../../product/rendering/frame-old';
import {FrameNone} from '../../product/rendering/frame-none';
import {ProductCustomizationState} from '../../product/rendering/render.def';

type Props = {
    pcs: ProductCustomizationState;
    frame: RenderV2Object;
};

let DPI: number;

export const inchToPx = (val: number) => `${val !== -1 ? val * DPI : 0}px`;

export const FrameBuilder = observer(({frame, pcs}: Props) => {
    if (pcs.CustomizationMethodID === 0) {
        return <FrameNone pcs={pcs} />;
    }

    if (pcs.CustomizationMethodID === 1) {
        return <FrameOld pcs={pcs} />;
    }

    const getGap = (prop: 'gapRight' | 'gapBelow', mode: 'first' | 'sum' = 'first') => {
        const elements = frame.displayMatrix.flatMap(row => row).filter(el => el.displayType === DisplayTypeEnum.Doc && el[prop] !== -1);

        if (mode === 'first') return elements[0]?.[prop];
        if (mode === 'sum') return elements.reduce((sum, el) => sum + el[prop], 0);
    };

    const gapRight = getGap('gapRight', 'sum');
    const gapBelow = getGap('gapBelow', 'sum');
    const maxCols = Math.max(...frame.displayMatrix.map(r => r.length));
    const colWidths = Array.from({length: maxCols}).map((_, colIdx) => Math.max(...frame.displayMatrix.map(row => (row[colIdx] ? (row[colIdx].width !== -1 ? row[colIdx].width : 0) : 0))));
    const rowHeights = frame.displayMatrix.map(row => Math.max(...row.map(item => (item.height !== -1 && item.displayType === DisplayTypeEnum.Doc ? item.height : 0))));
    const contentWidth = colWidths.reduce((a, b) => a + b, frame.railLeft + frame.railRight + (gapRight ? gapRight : 0));
    const contentHeight = rowHeights.reduce((a, b) => a + b, frame.railTop + frame.railBottom + (gapBelow ? gapBelow : 0));
    const mouldingTotalThickness = frame.moulding.mouldingThickness * 2;
    const frameWidth = contentWidth + mouldingTotalThickness;
    const frameHeight = contentHeight + mouldingTotalThickness;

    DPI = pcs.DPI;

    return (
        <div
            id="frame-builder-container"
            style={{height: 'auto'}}
        >
            <div
                id="frame-builder"
                style={{
                    width: inchToPx(frameWidth),
                    height: inchToPx(frameHeight),
                    background: frame.mat1ImageURL ? '' : frame.mat1Color,
                    backgroundImage: frame.mat1ImageURL ? `url("${frame.mat1ImageURL}")` : '',
                }}
            >
                <Moulding
                    height={contentHeight}
                    width={contentWidth}
                    frame={frame}
                />

                <InnerContent
                    maxCols={maxCols}
                    frame={frame}
                    width={contentWidth}
                    height={contentHeight}
                    columnGap={getGap('gapRight') as Inches}
                />

                <CustomItem frame={frame} />
            </div>
        </div>
    );
});
